import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import agent from '@services/apiService';
import useInterval from '@hooks/useInterval';
import usePrevious from '@hooks/usePrevious';
import useDelayedRender from '@hooks/useDelayedRender';
import { checkAuthStatus, checkAuthExpiry, heartBeat } from '@redux/reducers/application/Authentication';

import {
  CLAREITY_LOGIN,
} from '@redux/constants/ActionTypes';

const CHECK_AUTH_EXPIRY_COOKIE_INTERVAL_DELAY = 1000 * 60; // 1 minute
const SESSION_MODAL_RENDER_DELAY = 2000; // 2 seconds
const CHECK_EXPIRY_ON_NEGATIVE_REMAINING_TIME_DELAY = 2000; // 2 seconds

const AppSessionModal = () => {
  const dispatch = useDispatch();
  const authChecked = useSelector(({ authentication }) => authentication.authChecked);
  const isLoadingAuth = useSelector(({ authentication }) => authentication.isLoadingAuth);
  const isAuthenticated = useSelector(({ authentication }) => authentication.isAuthenticated);
  const isAuthExpiryCookiePresent = useSelector(({ authentication }) => authentication.isAuthExpiryCookiePresent);
  const prevIsAuthExpiryCookiePresent = usePrevious(isAuthExpiryCookiePresent);
  const isAuthCloseToExpiry = useSelector(({ authentication }) => authentication.isAuthCloseToExpiry);
  const minutesLeftToAuthExpiry = useSelector(({ authentication }) => authentication.minutesLeftToAuthExpiry);
  const delayedRender = useDelayedRender(SESSION_MODAL_RENDER_DELAY);
  const [isSessionRefresh, setIsSessionRefresh] = useState(false);

  useInterval(() => dispatch(checkAuthExpiry()), !isSessionRefresh && authChecked && isAuthenticated && !isLoadingAuth ? CHECK_AUTH_EXPIRY_COOKIE_INTERVAL_DELAY : null);

  useEffect(() => {
    if (prevIsAuthExpiryCookiePresent && !isAuthExpiryCookiePresent) {
      dispatch(checkAuthStatus());
    }
  }, [isAuthExpiryCookiePresent]);

  useEffect(() => {
    if (minutesLeftToAuthExpiry === -1) {
      setTimeout(() => dispatch(checkAuthExpiry()), CHECK_EXPIRY_ON_NEGATIVE_REMAINING_TIME_DELAY);
    }
  }, [minutesLeftToAuthExpiry]);

  const onRefreshSession = async () => {
    try {
      setIsSessionRefresh(true);
      await dispatch(heartBeat());
      dispatch(checkAuthExpiry());
      setIsSessionRefresh(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error: Session onRefreshSession', err);
    }
  };

  const onReauthenticate = () => {
    dispatch({ type: CLAREITY_LOGIN, payload: agent.Auth.ClareityLogin() });
  };

  // Use a delayed render so that we would avoid displaying the modal right before a Clareity redirection to login page.
  return delayedRender(() => (
    <>
      {/* Modal responsible for alerting the user that the application session will expire soon */}
      <Modal
        show={isAuthCloseToExpiry && authChecked && isAuthenticated && !isLoadingAuth}
        closeButton={false}
        backdrop="static"
        keyboard={false}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        {isAuthCloseToExpiry && (
          <>
            <Modal.Header>
              <Modal.Title>Application Session</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {minutesLeftToAuthExpiry > 0
                ? `Your session is about to expire in ${minutesLeftToAuthExpiry} minute${minutesLeftToAuthExpiry > 1 ? 's' : ''}.`
                : 'Your session is about to expire in less than a minute.'}
            </Modal.Body>
            <Modal.Footer>
              <Button name="btnRefreshSession" variant="primary" onClick={onRefreshSession}>
                <i className="far fa-sync-alt mr-2" />
                Refresh Session
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/*  Modal responsible for alerting the user that the application session has expired */}
      <Modal
        show={authChecked && !isAuthenticated && !isLoadingAuth}
        closeButton={false}
        backdrop="static"
        keyboard={false}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header>
          <Modal.Title>Application Session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Your login session has expired.
        </Modal.Body>
        <Modal.Footer>
          <Button name="btnLoginAgain" variant="primary" onClick={onReauthenticate}>
            <i className="far fa-sign-in-alt mr-2" />
            Login Again
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  ));
};

export default AppSessionModal;
